import { useEffect, useRef, useState } from "react";
import { ModalShell } from "../../ModalShell";
import { ANALYTICS_EVENT, recordEvent } from "../../../hooks/useAnalytics";
import { SUPPORT_EMAIL } from "../../../config/links";
import { buildSupportMailto } from "./supportMailto";
import dialogStyles from "../../../pages/admin/AdminFeatureFlags.module.css";
import styles from "./SupportModal.module.css";

type SupportModalProps = {
  readonly tenantId: string | null;
  readonly onClose: () => void;
};

// The customer support popup behind "Ask for support". The address is always
// shown as selectable text with a Copy button so customers whose device has no
// mail client (webmail-only, locked-down machines) can still reach us — the
// mailto CTA is just the convenient path for everyone else.
export const SupportModal = ({ tenantId, onClose }: SupportModalProps): JSX.Element => {
  const [copied, setCopied] = useState<boolean>(false);
  const [copyError, setCopyError] = useState<boolean>(false);
  const primaryRef = useRef<HTMLAnchorElement | null>(null);
  const titleId = "support-dialog-title";
  const mailto = buildSupportMailto(SUPPORT_EMAIL, tenantId);

  useEffect(() => primaryRef.current?.focus(), []);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
      setCopied(true);
      setCopyError(false);
      window.setTimeout(() => setCopied(false), 2000);
      void recordEvent({
        type: ANALYTICS_EVENT.supportClick,
        subject: tenantId ?? undefined,
        note: "support_email_copied",
      });
    } catch {
      // Blurred window (NotAllowedError) or no clipboard API — point the
      // customer at selecting the address manually instead of failing silently.
      setCopied(false);
      setCopyError(true);
    }
  };

  const handleOpenEmail = (): void => {
    void recordEvent({
      type: ANALYTICS_EVENT.supportClick,
      subject: tenantId ?? undefined,
      note: "support_email_mailto",
    });
  };

  return (
    <ModalShell onClose={onClose} titleId={titleId} size="sm">
      <header className={dialogStyles.dialogHeader}>
        <h3 className={dialogStyles.dialogTitle} id={titleId}>
          Get support
        </h3>
        <button type="button" className={dialogStyles.closeBtn} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>

      <div className={styles.body}>
        <p className={styles.lead}>
          Email our team and we'll get back to you — usually within a few hours.
        </p>

        <div className={styles.emailRow}>
          <span className={styles.email} data-testid="support-email">
            {SUPPORT_EMAIL}
          </span>
          <button
            type="button"
            className={styles.copyBtn}
            data-copied={copied}
            onClick={() => void handleCopy()}
            data-testid="support-copy-email"
          >
            {copied ? "Copied ✓" : "Copy"}
          </button>
        </div>

        {copyError && (
          <p className={styles.copyHint} role="alert">
            Couldn't copy automatically — select the address above and copy it manually.
          </p>
        )}

        <a
          ref={primaryRef}
          className={styles.primaryBtn}
          href={mailto}
          onClick={handleOpenEmail}
          data-testid="support-open-email"
        >
          Open email app
        </a>

        <p className={styles.hint}>
          Nothing happens when you click? Your device may not have an email app set up — copy the
          address above and email us from Gmail, Outlook, or wherever you read your email.
        </p>
      </div>
    </ModalShell>
  );
};
